/**
 * Tenant-scoped drill-through link registry. Authors register named
 * targets (reports / dashboards / external URLs) with a declared
 * parameter contract; widgets and {{reportLink}} reference them by id.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export type DrillKind = 'template' | 'dashboard' | 'external';
export type DrillParamKind = 'string' | 'integer' | 'date' | 'boolean';

export interface DrillParam {
  name: string;
  required: boolean;
  kind: DrillParamKind;
}

export interface DrillTarget {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  targetKind: DrillKind;
  targetId: string | null;
  externalUrl: string | null;
  paramContract: DrillParam[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useDrillTargets() {
  const api = useApi();
  return useQuery<DrillTarget[]>({
    queryKey: ['drill-targets'],
    queryFn: () => api.fetch('/drill-targets'),
    staleTime: 60_000,
  });
}

export function useCreateDrillTarget() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      description?: string;
      targetKind: DrillKind;
      targetId?: string;
      externalUrl?: string;
      paramContract?: DrillParam[];
    }) =>
      api.fetch<DrillTarget>('/drill-targets', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drill-targets'] });
    },
  });
}

export function useUpdateDrillTarget() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<DrillTarget> }) =>
      api.fetch<DrillTarget>(`/drill-targets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drill-targets'] });
    },
  });
}

export function useDeleteDrillTarget() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.fetchRaw(`/drill-targets/${id}`, { method: 'DELETE' }).then((r) => {
        if (!r.ok && r.status !== 204) throw new Error(`Delete failed: ${r.status}`);
        return true;
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drill-targets'] });
    },
  });
}
